import React, { useState } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, ArrowLeft, Sparkles, Loader } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import axios from 'axios';

/**
 * Screen 2: CSV Upload with Smart Validation
 *
 * Validates that uploaded CSV has required columns for selected enrichment types.
 * Shows field mapping interface if multiple possible matches exist.
 */
const EnrichmentCSVUpload = ({ selectedTypes, onBack, onSubmit }) => {
  const [jobName, setJobName] = useState('');
  const [sourceFile, setSourceFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [fieldMappings, setFieldMappings] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Get enrichment type names for display
  const enrichmentTypeNames = {
    company_email: 'Company Email',
    company_phone: 'Company Phone',
    company_address: 'Company Address',
    company_industry: 'Industry Classification',
    company_size: 'Company Size',
    company_revenue: 'Revenue',
    company_executive: 'Executive Contact',
    company_social: 'Social Media Profiles',
    company_tech_stack: 'Technology Stack',
    procurement_contact: 'Procurement Contact'
  };

  const parseCSVHeaders = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;

          // Check for binary data (Excel file)
          const nonPrintableCount = (text.match(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g) || []).length;
          if (nonPrintableCount > 10) {
            console.log('Binary file detected (Excel)');
            resolve([]);
            return;
          }

          const lines = text.split('\n');
          if (lines.length > 0) {
            const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
            const cleanHeaders = headers.filter(h => h.length > 0 && !/[\x00-\x1F]/.test(h));
            resolve(cleanHeaders);
          } else {
            resolve([]);
          }
        } catch (err) {
          console.error('Error parsing CSV headers:', err);
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('Please upload a CSV or Excel file');
      return;
    }

    setSourceFile(file);
    setError(null);
    setValidationResult(null);

    // Parse CSV headers for validation
    if (file.name.endsWith('.csv') && file.type === 'text/csv') {
      try {
        setIsValidating(true);
        const headers = await parseCSVHeaders(file);
        setCsvHeaders(headers);

        // Validate against selected enrichment types
        await validateCSV(headers);
      } catch (err) {
        console.error('Error parsing CSV:', err);
        setError('Failed to parse CSV file. Please check the file format.');
      } finally {
        setIsValidating(false);
      }
    } else {
      // Excel file - will be validated on backend
      setCsvHeaders([]);
      setValidationResult({
        valid: true,
        message: 'Excel file will be validated during processing'
      });
    }
  };

  const validateCSV = async (headers) => {
    try {
      // Call backend validation API
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/jobs/enrichment/validate-csv`,
        {
          csv_headers: headers,
          selected_types: selectedTypes
        }
      );

      const result = response.data;
      setValidationResult(result);

      // Initialize field mappings with suggestions
      if (result.suggestions && result.suggestions.length > 0) {
        const mappings = {};
        result.suggestions.forEach(suggestion => {
          // Auto-select first suggestion
          if (suggestion.possible_columns && suggestion.possible_columns.length > 0) {
            mappings[`${suggestion.enrichment_type}_${suggestion.field}`] = suggestion.possible_columns[0];
          }
        });
        setFieldMappings(mappings);
      }
    } catch (err) {
      console.error('Error validating CSV:', err);
      setValidationResult({
        valid: false,
        errors: [err.response?.data?.detail || 'Failed to validate CSV']
      });
    }
  };

  const handleSubmit = async () => {
    if (!sourceFile || !jobName) {
      alert('Please provide a job name and upload a CSV file');
      return;
    }

    if (validationResult && !validationResult.valid) {
      alert('Please fix CSV validation errors before submitting');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', sourceFile);
      formData.append('job_name', jobName);
      formData.append('enrichment_types', JSON.stringify(selectedTypes));
      formData.append('field_mappings', JSON.stringify(fieldMappings));

      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting enrichment job:', err);
      setError(err.response?.data?.detail || 'Failed to create enrichment job');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'var(--gradient-brand)',
            color: 'white'
          }}>
            <FileSpreadsheet size={24} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-primary)' }}>
              Upload Your CSV
            </h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted)' }}>
              We'll validate it has the required columns (Step 2 of 2)
            </p>
          </div>
        </div>
      </div>

      {/* Selected Types Summary */}
      <Card style={{ marginBottom: 20 }}>
        <Card.Header>
          <Card.Title>Selected Enrichment Types</Card.Title>
        </Card.Header>
        <Card.Content>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {selectedTypes.map(typeId => (
              <span
                key={typeId}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'var(--color-info-bg)',
                  color: 'var(--color-info-fg)',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 500
                }}
              >
                {enrichmentTypeNames[typeId] || typeId}
              </span>
            ))}
          </div>
        </Card.Content>
      </Card>

      {/* Job Name */}
      <Card style={{ marginBottom: 20 }}>
        <Card.Header>
          <Card.Title>Job Configuration</Card.Title>
        </Card.Header>
        <Card.Content>
          <FormField
            label="Job Name"
            type="text"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            placeholder="e.g., Q1 2024 Prospect Enrichment"
            required
          />
        </Card.Content>
      </Card>

      {/* File Upload */}
      <Card style={{ marginBottom: 20 }}>
        <Card.Header>
          <Card.Title>Upload CSV File</Card.Title>
        </Card.Header>
        <Card.Content>
          <div style={{
            border: '2px dashed var(--color-border-strong)',
            borderRadius: 8,
            padding: 32,
            textAlign: 'center',
            backgroundColor: 'var(--color-bg-subtle)'
          }}>
            <Upload size={32} color="#9ca3af" style={{ margin: '0 auto 12px' }} />
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 12 }}>
              Upload CSV or Excel file with your company data
            </p>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="csv-upload"
            />
            <label htmlFor="csv-upload">
              <Button type="button" onClick={() => document.getElementById('csv-upload').click()}>
                <FileSpreadsheet size={16} style={{ marginRight: 8 }} />
                Choose File
              </Button>
            </label>

            {sourceFile && (
              <div style={{
                marginTop: 16,
                padding: 12,
                backgroundColor: 'var(--color-success-bg)',
                border: '1px solid var(--color-success-border)',
                borderRadius: 6,
                display: 'inline-block'
              }}>
                <span style={{ color: 'var(--color-success-fg)', fontSize: 14, fontWeight: 500 }}>
                  ✓ {sourceFile.name}
                </span>
                {csvHeaders.length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
                    {csvHeaders.length} columns: {csvHeaders.slice(0, 3).join(', ')}
                    {csvHeaders.length > 3 && `, +${csvHeaders.length - 3} more`}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Validation in progress */}
          {isValidating && (
            <div style={{
              marginTop: 16,
              padding: 12,
              backgroundColor: 'var(--color-info-bg)',
              border: '1px solid var(--color-info-border)',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <Loader size={16} className="animate-spin" color="#3b82f6" />
              <span style={{ color: 'var(--color-info-fg)', fontSize: 14 }}>
                Validating CSV...
              </span>
            </div>
          )}

          {/* Validation Results */}
          {validationResult && !isValidating && (
            <div style={{ marginTop: 16 }}>
              {validationResult.valid ? (
                <div style={{
                  padding: 12,
                  backgroundColor: 'var(--color-success-bg)',
                  border: '1px solid var(--color-success)',
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8
                }}>
                  <CheckCircle size={20} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#065f46', marginBottom: 4 }}>
                      CSV Validated Successfully
                    </div>
                    <div style={{ fontSize: 13, color: '#047857' }}>
                      {validationResult.message || 'Your CSV has all required columns for the selected enrichment types.'}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: 12,
                  backgroundColor: 'var(--color-danger-bg)',
                  border: '1px solid var(--color-danger)',
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8
                }}>
                  <AlertCircle size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--color-danger-fg)', marginBottom: 8 }}>
                      CSV Validation Errors
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--color-danger-hover)', fontSize: 13 }}>
                      {validationResult.errors?.map((error, index) => (
                        <li key={index} style={{ marginBottom: 4 }}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Field Mapping Suggestions */}
              {validationResult.suggestions && validationResult.suggestions.length > 0 && (
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  backgroundColor: '#fefce8',
                  border: '1px solid #facc15',
                  borderRadius: 6
                }}>
                  <div style={{ fontWeight: 600, color: '#854d0e', marginBottom: 12 }}>
                    ⚠️ Please confirm column mappings:
                  </div>
                  {validationResult.suggestions.map((suggestion, index) => (
                    <div key={index} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, color: '#713f12', marginBottom: 6 }}>
                        <strong>{suggestion.enrichment_type}</strong> - {suggestion.field}:
                      </div>
                      <select
                        value={fieldMappings[`${suggestion.enrichment_type}_${suggestion.field}`] || ''}
                        onChange={(e) => setFieldMappings({
                          ...fieldMappings,
                          [`${suggestion.enrichment_type}_${suggestion.field}`]: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: '1px solid var(--color-border-strong)',
                          fontSize: 14
                        }}
                      >
                        <option value="">-- Select Column --</option>
                        {suggestion.possible_columns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card.Content>
      </Card>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: 16,
          backgroundColor: 'var(--color-danger-bg-strong)',
          border: '1px solid var(--color-danger)',
          borderRadius: 8,
          color: 'var(--color-danger-fg)',
          marginBottom: 20
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: 12,
        paddingTop: 24,
        borderTop: '1px solid var(--color-border)'
      }}>
        <Button
          variant="secondary"
          onClick={onBack}
        >
          <ArrowLeft size={16} style={{ marginRight: 8 }} />
          Back to Type Selection
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!sourceFile || !jobName || (validationResult && !validationResult.valid) || isSubmitting}
          style={{ flex: 1 }}
        >
          {isSubmitting ? (
            <>
              <Loader size={16} style={{ marginRight: 8 }} className="animate-spin" />
              Creating Job...
            </>
          ) : (
            <>
              <Sparkles size={16} style={{ marginRight: 8 }} />
              Create Enrichment Job
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EnrichmentCSVUpload;
